import { useState, useEffect } from 'react'
import { Ticket, User } from '../types'
import { useAuth } from '../hooks/useAuth'
import { loadExecutors, loadUsers } from '../lib/api'
import { downloadFile } from '../utils/fileDownload'
import './AssignExecutorModal.css'

interface AssignExecutorModalProps {
  ticket: Ticket
  onAssign: (ticketId: string, executorId: string, comment?: string) => void | Promise<void>
  onClose: () => void
}

function AssignExecutorModal({ ticket, onAssign, onClose }: AssignExecutorModalProps) {
  const { user } = useAuth()
  const isChange = ticket.status === 'in_progress' && Boolean(ticket.executor_id)
  const [selectedExecutor, setSelectedExecutor] = useState(ticket.executor_id || '')
  const [comment, setComment] = useState('')
  const [executors, setExecutors] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadExecutorsData()
  }, [])

  const loadExecutorsData = async () => {
    try {
      setLoading(true)
      const [executorsData, adminsData] = await Promise.all([
        loadExecutors(),
        loadUsers().then(users => users.filter(u => u.role === 'admin' && u.admin_type === 'spravce_budov'))
      ])

      const allAvailableExecutors = [...executorsData, ...adminsData]

      if (user && user.role === 'admin') {
        const userIndex = allAvailableExecutors.findIndex(e => e.id === user.id)
        if (userIndex !== -1) {
          const userExecutor = allAvailableExecutors[userIndex]
          const otherExecutors = allAvailableExecutors.filter(e => e.id !== user.id)
          setExecutors([userExecutor, ...otherExecutors])
        } else {
          setExecutors([user, ...allAvailableExecutors])
        }
      } else {
        setExecutors(allAvailableExecutors)
      }
    } catch (error) {
      console.error('Chyba při načítání řešitelů:', error)
      alert('Chyba při načítání řešitelů')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedExecutor) {
      alert('Prosím vyberte řešitele')
      return
    }
    try {
      setSubmitting(true)
      await onAssign(ticket.id, selectedExecutor, comment.trim() || undefined)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isChange ? 'Změnit řešitele' : 'Přiřadit řešitele'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="ticket-preview">
            <p><strong>Žádost #{ticket.id}</strong></p>
            <p><strong>Budova:</strong> {ticket.building_name || 'Neznámá'}</p>
            <p><strong>Žadatel:</strong> {ticket.requester_name || 'Neznámý'}</p>
            {ticket.executor_name && (
              <p><strong>Současný řešitel:</strong> {ticket.executor_name}</p>
            )}
            {ticket.requester_email && (
              <p>
                <strong>Email:</strong>{' '}
                <a href={`mailto:${ticket.requester_email}`} className="email-link">
                  {ticket.requester_email}
                </a>
              </p>
            )}
            {ticket.phone && (
              <p>
                <strong>Telefon:</strong>{' '}
                <a href={`tel:${ticket.phone}`} className="phone-link">
                  {ticket.phone}
                </a>
              </p>
            )}
            <p><strong>Popis:</strong></p>
            <p style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem', color: '#555' }}>{ticket.description}</p>
            {ticket.file_name && ticket.file_url && (
              <p>
                <strong>Příloha:</strong>{' '}
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault()
                    try {
                      await downloadFile(ticket.file_url || ticket.file_name || '', ticket.id, undefined, false)
                    } catch (error: any) {
                      alert('Chyba při otevírání souboru: ' + (error.message || 'Neznámá chyba'))
                    }
                  }}
                  className="file-link"
                  title={`Otevřít přílohu: ${ticket.file_name}`}
                >
                  📎 {ticket.file_name}
                </a>
                <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                  <a
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault()
                      try {
                        await downloadFile(ticket.file_url || ticket.file_name || '', ticket.id, undefined, true)
                      } catch (error: any) {
                        alert('Chyba při stahování souboru: ' + (error.message || 'Neznámá chyba'))
                      }
                    }}
                    className="file-link"
                    title="Stáhnout soubor"
                  >
                    ⬇️ Stáhnout
                  </a>
                </span>
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="executor">
                Vyberte řešitele <span className="required">*</span>
              </label>
              <select
                id="executor"
                value={selectedExecutor}
                onChange={(e) => setSelectedExecutor(e.target.value)}
                required
                disabled={loading || submitting}
              >
                <option value="">{loading ? 'Načítání...' : 'Vyberte řešitele'}</option>
                {executors.map(executor => (
                  <option key={executor.id} value={executor.id}>
                    {executor.name}
                    {executor.role === 'admin' && executor.admin_type === 'spravce_it' && ' (IT Admin)'}
                    {executor.role === 'admin' && executor.admin_type === 'spravce_budov' && ' (Správce Budov)'}
                    {executor.role === 'executor' && ' (Řešitel)'}
                    {executor.role === 'vedouci_executor' && ' (Řešitel-Vedoucí)'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="assign-comment">
                Komentář {isChange ? 'ke změně' : 'k přiřazení'} (volitelné)
              </label>
              <textarea
                id="assign-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  isChange
                    ? 'Např. důvod změny řešitele, poznámka pro nového řešitele…'
                    : 'Např. poznámka pro řešitele…'
                }
                rows={4}
                maxLength={1000}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                Komentář se přidá k popisu žádosti se jménem a časem.
              </p>
            </div>

            <div className="modal-actions">
              <button type="button" onClick={onClose} className="cancel-button" disabled={submitting}>
                Zrušit
              </button>
              <button type="submit" className="submit-button" disabled={submitting || loading}>
                {submitting ? 'Ukládání...' : isChange ? 'Změnit řešitele' : 'Přiřadit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AssignExecutorModal
